import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} [addToCartButton] - Sticky bar's native cart button
 * @property {HTMLElement} [quantityDisplay] - Quantity display container
 * @property {HTMLElement} [quantityNumber] - Quantity number element
 * @property {HTMLElement} [priceDisplay] - Current purchase total display
 * @property {HTMLElement} [purchaseSummary] - Visible purchase type, cadence, and can count
 * @property {HTMLImageElement} [productImage] - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {number | null} */
  #currentValueCents = null;

  /** Display-only offer amount; native purchase values remain unchanged. */
  #currentDisplayValueCents = null;


  /** @type {'one_time' | 'subscription'} */
  #currentPurchaseType = 'one_time';

  /** @type {string} */
  #currentSellingPlanName = '';

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('proda:purchase-option-change', this.#handleProdaPurchaseChange, { signal });
    document.addEventListener('proda:pack-size-change', this.#handleProdaPurchaseChange, { signal });
    document.addEventListener('proda:offer-prices-change', this.#handleOfferPricesChange, { signal });

    this.#getInitialQuantity();
    this.#syncInitialProdaPurchaseSummary();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      // `finally` returns a second promise. Consume its rejection so an intentionally
      // superseded cart quantity response cannot surface as an unhandled rejection.
      void event.promise.finally(resetPuppet).catch(() => {});
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Keeps the mobile sticky purchase action aligned with the selected PRODA offer.
   * @param {CustomEvent} event - Purchase option or pack size change event
   */
  #handleProdaPurchaseChange = (event) => {
    const detail = event.detail ?? {};
    if (String(detail.productId || '') !== String(this.dataset.productId || '')) return;

    this.#applyProdaPurchaseSummary(detail);
  };

  #handleOfferPricesChange = () => {
    const purchaseOptions = this.#getProductForm()?.querySelector('proda-purchase-options');
    if (typeof purchaseOptions?.purchaseSummary === 'function') {
      this.#applyProdaPurchaseSummary(purchaseOptions.purchaseSummary());
    }
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Reads the already-selected purchase controls after their custom element upgrades.
   */
  #syncInitialProdaPurchaseSummary() {
    if (!this.dataset.cansPerUnit) return;

    const { signal } = this.#abortController;
    customElements.whenDefined('proda-purchase-options').then(() => {
      if (signal.aborted) return;

      const productForm = this.#getProductForm();
      const purchaseOptions = productForm?.querySelector('proda-purchase-options');
      if (!purchaseOptions || typeof purchaseOptions.purchaseSummary !== 'function') return;

      this.#applyProdaPurchaseSummary(purchaseOptions.purchaseSummary());
    });
  }

  /**
   * @param {{caseQuantity?: number, canCount?: number, valueCents?: number, purchaseType?: string, isSubscription?: boolean, sellingPlanName?: string, selectedPlan?: HTMLOptionElement}} summary
   */
  #applyProdaPurchaseSummary(summary) {
    const caseQuantity = Number(summary.caseQuantity || 0);
    const canCount = Number(summary.canCount || 0);
    const valueCents = Number(summary.valueCents);

    if (caseQuantity > 0) this.#currentQuantity = caseQuantity;
    if (Number.isFinite(valueCents)) this.#currentValueCents = valueCents;
    this.#currentPurchaseType =
      summary.purchaseType === 'subscription' || summary.isSubscription === true ? 'subscription' : 'one_time';
    this.#currentSellingPlanName =
      summary.sellingPlanName || summary.selectedPlan?.dataset?.planName || this.#currentSellingPlanName;

    let offerQuote = null;
    const purchaseOptions = this.#getProductForm()?.querySelector('proda-purchase-options');
    if (this.#currentPurchaseType === 'one_time' && typeof window.PRODA_OFFER_PRICES?.quote === 'function' && purchaseOptions) {
      try {
        offerQuote = window.PRODA_OFFER_PRICES.quote({
          handle: purchaseOptions.dataset.productHandle,
          variantId: purchaseOptions.dataset.variantId,
          baseCents: Number(purchaseOptions.dataset.oneTimePriceCents),
          quantity: this.#currentQuantity,
          purchaseType: 'one_time',
        });
      } catch { /* Keep the native price if an optional offer preview is unavailable. */ }
    }
    this.#currentDisplayValueCents = offerQuote?.valueCents ?? this.#currentValueCents;
    if (this.refs.priceDisplay && Number.isFinite(this.#currentDisplayValueCents)) {
      this.refs.priceDisplay.textContent = this.#formatMoney(this.#currentDisplayValueCents);
    }

    if (canCount > 0) this.dataset.currentCanCount = String(canCount);
    this.#updateButtonText();
  }

  /**
   * @param {number} valueCents
   * @returns {string}
   */
  #formatMoney(valueCents) {
    const currency = this.dataset.currencyCode || window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(valueCents / 100);
    } catch (error) {
      return `$${(valueCents / 100).toFixed(2)}`;
    }
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const cansPerUnit = parseInt(this.dataset.cansPerUnit || '0') || 0;
    const canCount = parseInt(this.dataset.currentCanCount || '0') || this.#currentQuantity * cansPerUnit;
    this.#updatePurchaseSummary(canCount);

    // External BUY NOW products render a direct link and do not have quantity refs.
    if (!addToCartButton || !quantityDisplay || !quantityNumber) return;

    const available = !addToCartButton.disabled;

    // The direct-purchase preview merchandises case quantity as can count.
    quantityNumber.textContent = cansPerUnit ? `${canCount} cans` : this.#currentQuantity.toString();

    if (cansPerUnit > 0) {
      const productTitle = this.dataset.productTitle || 'this product';
      const priceLabel = this.#currentDisplayValueCents === null ? '' : ` for ${this.#formatMoney(this.#currentDisplayValueCents)}`;
      addToCartButton.setAttribute('aria-label', `Add ${canCount} cans of ${productTitle} to cart${priceLabel}`);
    }

    // Show/hide the quantity display based on availability and quantity
    if (available && (cansPerUnit > 0 || this.#currentQuantity > 1)) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }

  /**
   * Keeps the recurring commitment visible after the main purchase controls leave the viewport.
   * @param {number} canCount
   */
  #updatePurchaseSummary(canCount) {
    if (!this.refs.purchaseSummary || canCount <= 0) return;

    if (this.#currentPurchaseType !== 'subscription') {
      this.refs.purchaseSummary.textContent = `One-time · ${canCount} cans`;
      return;
    }

    const cadence = this.#currentSellingPlanName
      .replace(/^Deliver\s+/i, '')
      .replace(/,\s*\d+%\s*off.*$/i, '')
      .trim();
    this.refs.purchaseSummary.textContent = `Subscription${cadence ? ` · ${cadence}` : ''} · ${canCount} cans`;
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
